import React from 'react';
import * as types from '../../api/types';
import { formatJobAssignee, formatJobDate, formatJobMoney } from '../../utils/jobPresentation';
import { jobStatusLabel } from '../jobUi';
import { ChecklistPrintBlock } from './ChecklistPrintBlock';
import { chipPlainText, enrollmentRefOf } from '../../features/service-bundles/bundleUi';

const cell = 'px-2 py-2 align-top';
const numericCell = `${cell} text-right whitespace-nowrap`;
const PRICED_HEADINGS = ['Work item', 'Qty', 'Unit price', 'Net', 'Tax', 'Total'];
const SERVICE_HEADINGS = ['Work item', 'Qty'];

export interface ServiceOrderDocumentProps {
  job: types.Job;
  /**
   * Bulk work orders (tracker item 58). Omitted: the individual service order
   * of /print/jobs/:id, unchanged. 'SERVICE': a SERVICE COPY that renders no
   * unit price, net, tax or total at all (not hidden with CSS). 'BILLING': a
   * BILLING COPY with the server's decimal-string amounts.
   */
  copy?: types.WorkOrderCopyType;
  /** Prior confirmed print history: a REPRINT stamp. */
  reprint?: boolean;
  /**
   * The job's checklist steps (tracker item 44), printed on every copy type
   * (no prices involved). Omitted, null or without steps: no checklist block.
   */
  checklist?: types.JobChecklist | null;
}

/** Shown on paper for each copy type. */
export const COPY_LABELS: Record<types.WorkOrderCopyType, string> = {
  SERVICE: 'SERVICE COPY',
  BILLING: 'BILLING COPY',
};

/**
 * The service order (job ticket) body a technician takes to the property.
 * Uses only what GET /jobs/:id embeds (customer, property, assignee).
 */
export function ServiceOrderDocument({ job, copy, reprint = false, checklist = null }: ServiceOrderDocumentProps) {
  const location = [job.property.address, [job.property.city, job.property.state].filter(Boolean).join(', ')].filter(Boolean).join(', ');
  const showAmounts = copy !== 'SERVICE';
  const bundleRef = enrollmentRefOf(job);
  const headings = showAmounts ? PRICED_HEADINGS : SERVICE_HEADINGS;
  return (
    <>
      <section className="print-keep mb-8 flex flex-wrap items-start justify-between gap-6">
        <div>
          <h1 className="text-2xl font-bold tracking-wide">SERVICE ORDER</h1>
          <p className="mt-1 text-gray-700">Job {job.id}</p>
          {/* Service bundle visit (tracker item 85): plain text, printed on every copy type */}
          {bundleRef && <p data-testid="print-bundle" className="mt-1 font-semibold text-gray-900">{chipPlainText(bundleRef)}</p>}
          {(copy || reprint) && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {copy && (
                <p data-copy-label={copy} className="inline-block border-2 border-gray-900 px-3 py-1 text-lg font-black tracking-[0.2em]">
                  {COPY_LABELS[copy]}
                </p>
              )}
              {/* Border and text colour only: browsers drop background graphics when printing. */}
              {reprint && (
                <p data-mark="REPRINT" className="inline-block -rotate-2 border-4 border-red-700 px-3 py-0.5 text-lg font-black tracking-[0.3em] text-red-700">
                  REPRINT
                </p>
              )}
            </div>
          )}
        </div>
        <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
          <dt className="text-gray-600">Status</dt><dd>{jobStatusLabel(job.status)}</dd>
          <dt className="text-gray-600">Scheduled</dt><dd>{formatJobDate(job.scheduledDate)}</dd>
          {copy && job.scheduledStartTime && <><dt className="text-gray-600">Start</dt><dd>{job.scheduledStartTime}</dd></>}
          {job.completedDate && <><dt className="text-gray-600">Completed</dt><dd>{formatJobDate(job.completedDate)}</dd></>}
          <dt className="text-gray-600">Technician</dt><dd>{formatJobAssignee(job.assignedToUser)}</dd>
        </dl>
      </section>

      <section className="print-keep mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 print:grid-cols-2">
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Customer</h2>
          <p className="font-semibold">{job.customer.name}</p>
        </div>
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Service address</h2>
          <p>{location}</p>
        </div>
      </section>

      <h2 className="mb-2 text-base font-semibold">Work items</h2>
      <div className="overflow-x-auto print:overflow-visible">
        <table className="print-table w-full border-collapse text-sm" aria-label="Work items">
          <thead>
            <tr className="border-b-2 border-gray-800">
              {headings.map((heading, index) => <th key={heading} className={`${index === 0 ? 'text-left' : 'text-right'} px-2 py-2 font-semibold`}>{heading}</th>)}
            </tr>
          </thead>
          <tbody>
            {job.lineItems.length === 0
              ? <tr><td colSpan={headings.length} className={`${cell} text-gray-600`}>No work items.</td></tr>
              : job.lineItems.map((line) => (
                <tr key={line.id} className="border-b border-gray-200">
                  <td className={cell}>
                    <span className="font-medium">{line.item.name}</span>
                    {line.item.description && <span className="block text-xs text-gray-600">{line.item.description}</span>}
                  </td>
                  <td className={numericCell}>{line.quantity}</td>
                  {showAmounts && (
                    <>
                      <td className={numericCell}>{formatJobMoney(line.unitPrice)}</td>
                      <td className={numericCell}>{formatJobMoney(line.lineTotal)}</td>
                      <td className={numericCell}>{formatJobMoney(line.lineTax)}</td>
                      <td className={numericCell}>{formatJobMoney(line.lineAmount)}</td>
                    </>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {showAmounts && (
        <dl className="print-keep mt-6 ml-auto max-w-xs space-y-1" aria-label="Job totals">
          <div className="flex justify-between gap-6"><dt>Subtotal</dt><dd>{formatJobMoney(job.subtotal)}</dd></div>
          <div className="flex justify-between gap-6"><dt>Tax</dt><dd>{formatJobMoney(job.totalTax)}</dd></div>
          <div className="flex justify-between gap-6 border-t-2 border-gray-800 pt-2 text-base font-bold"><dt>Total</dt><dd>{formatJobMoney(job.total)}</dd></div>
        </dl>
      )}

      <ChecklistPrintBlock checklist={checklist} />

      {/* Long notes may run across pages, so this block is allowed to break. */}
      <section className="mt-8">
        <h2 className="mb-1 text-base font-semibold">Job notes</h2>
        <p className="whitespace-pre-wrap break-words">{job.notes?.trim() || 'No notes'}</p>
      </section>

      <section className="print-keep mt-8" aria-label="Technician notes">
        <h2 className="mb-2 text-base font-semibold">Technician notes</h2>
        {[0, 1, 2, 3, 4, 5].map((row) => <div key={row} className="h-8 border-b border-gray-400" />)}
      </section>

      <section className="print-keep mt-12 grid grid-cols-1 gap-10 sm:grid-cols-2 print:grid-cols-2" aria-label="Signatures">
        {['Customer signature', 'Technician signature'].map((label) => (
          <div key={label}>
            <div className="h-10 border-b border-gray-800" />
            <div className="mt-1 flex justify-between text-xs text-gray-700"><span>{label}</span><span>Date</span></div>
          </div>
        ))}
      </section>
    </>
  );
}
